import type { FC, ReactNode } from 'react'
// reason: geen zin in bazel gedoe in receiver app die anders heel dhl-ui onderdelen wil als module...
// eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
import { Box, type SxProps, type Theme, Typography } from '@mui/material'
import { type JSXFunctionSerializer, PrismicRichText } from '@prismicio/react'
import type { RichTextField, RichTextNodeTypes, RTAnyNode } from '@prismicio/client'

const sx = {
  paragraphSx: {
    mt: 2,
    '& > strong': {
      fontFamily: '"Delivery Bold", Calibri, Arial, sans-serif',
    },
  },
  imageSx: {
    maxHeight: 200,
    maxWidth: '100%',
    margin: 0,
    mt: 2,
  },
}

type Props = {
  field: RichTextField | null | undefined
  paragraphSx?: SxProps<Theme>
  bodyVariant?: 'body1' | 'body2'
}

export const Contents: FC<Props> = ({ field, paragraphSx = sx.paragraphSx, bodyVariant = 'body1' }) => {
  const htmlSerializer: JSXFunctionSerializer = (
    type: RichTextNodeTypes,
    element: RTAnyNode,
    text: string | undefined,
    children: ReactNode[],
    key: string
  ) => {
    switch (element.type) {
      case 'strong':
        return text ? <strong key={key}>{text}</strong> : null

      case 'image':
        return (
          <Box key={element.url} display="flex">
            <Box component="img" src={element.url} alt={element.alt || undefined} sx={sx.imageSx} />
          </Box>
        )

      case 'heading1':
      case 'heading2':
      case 'heading3':
      case 'heading4':
      case 'heading5':
      case 'heading6':
        // reason: type is another type then ours but basically we set html headings in their visual version
        // eslint-disable-next-line no-case-declarations
        const variant = `h${type.replace('heading', '')}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
        // TODO: we are missing a Title component where i can give the h1 the look of h3 and vice versa
        // at moment we dont have any compositions, worst UI implementation to let consumers directly take from MUI
        return (
          <Typography key={key} variant={variant}>
            {children}
          </Typography>
        )

      case 'paragraph':
        if (!element.text) {
          return null
        }
        return (
          <Typography key={key} variant={bodyVariant} component="p" sx={paragraphSx}>
            {children}
          </Typography>
        )

      case 'list-item':
      case 'o-list-item':
        if (!element.text) {
          return null
        }
        return (
          <Typography key={key} variant={bodyVariant} component="li">
            {children}
          </Typography>
        )

      case 'embed':
        if (!element.oembed.embed_url) {
          return null
        }

        // reason: embed element doesn't accept embed urls, convert so we can use it in iframe
        // eslint-disable-next-line no-case-declarations
        const embedUrl = element.oembed.embed_url
          .replace('youtu.be', 'youtube.com/embed')
          .replace('youtube.com/watch?v=', 'youtube.com/embed/')

        return (
          <Box pt={2} maxWidth="480px">
            <iframe width="100%" height="250" src={embedUrl} title={element.oembed.title || ''} allowFullScreen />
          </Box>
        )
      default:
        // null means default behaviour
        return null
    }
  }

  return <PrismicRichText field={field} components={htmlSerializer} />
}
